// =============================================================================
// conversationToText — Serialize DM conversations to plain text
// =============================================================================
// PURPOSE: Convert a list of GHL messages + metadata into a plain-text
// transcript suitable for clipboard copy, AI analysis, or record-keeping.
//
// Handles both "raw" DB message shapes (ghl_messages columns) and
// "normalized" shapes (already transformed by frontend hooks).
//
// CRITICAL: Use Intl with explicit 'en-US' locale everywhere (CLAUDE.md bug #16 —
// Australian/European browsers produce DD/MM/YYYY which breaks date parsing).
// =============================================================================

export type SerializerRawMessage = {
  message_text: string | null;
  sender_type: string; // 'lead' | 'setter' | 'system' (lowercase)
  sent_at: string; // ISO timestamp UTC
  has_media?: boolean | null;
  sender_name?: string | null;
  ghl_sender_user_id?: string | null;
};

export type SerializerNormalizedMessage = {
  text?: string | null;
  sender: "lead" | "setter" | "system";
  sent_at: string;
  has_media?: boolean | null;
};

export type SerializerMessage = SerializerRawMessage | SerializerNormalizedMessage;

// Type guards
function isRawMessage(msg: SerializerMessage): msg is SerializerRawMessage {
  return "message_text" in msg;
}

// ---------------------------------------------------------------------------
// Timestamp formatting
// ---------------------------------------------------------------------------
// Output format: "Apr 22, 2026 09:14 SGT"
// We build this manually because formatDateTimeInTZ (lib/timezone.ts) produces
// "Apr 22, 9:14 AM" — different format, no year, 12h, no tz abbreviation.
// ---------------------------------------------------------------------------

function getTimezoneAbbr(tz: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "short",
  }).formatToParts(new Date());
  return parts.find((p) => p.type === "timeZoneName")?.value || tz;
}

function formatTimestamp(utcISO: string, tz: string): string {
  const d = new Date(utcISO);
  const fmt = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
  });

  // formatToParts gives us clean access to each part without locale ambiguity
  const parts = fmt.formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  const month = get("month"); // "Apr"
  const day = get("day"); // "22"
  const year = get("year"); // "2026"
  const hour = get("hour"); // "09"
  const minute = get("minute"); // "14"
  const abbr = getTimezoneAbbr(tz);

  return `${month} ${day}, ${year} ${hour}:${minute} ${abbr}`;
}

// ---------------------------------------------------------------------------
// Date range formatting (header line)
// NEVER use bare toLocaleDateString — must pass 'en-US' (CLAUDE.md bug #16).
// ---------------------------------------------------------------------------

function formatDateRange(utcISO: string, tz: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: tz,
  }).format(new Date(utcISO));
}

// ---------------------------------------------------------------------------
// Sender label logic
// ---------------------------------------------------------------------------

function getSenderLabel(msg: SerializerMessage): string {
  if (isRawMessage(msg)) {
    const senderType = msg.sender_type?.toLowerCase() ?? "";
    if (senderType === "setter") {
      // ManyChat: setter message with no human sender ID
      if (!msg.ghl_sender_user_id) {
        return "[ManyChat]";
      }
      const name = msg.sender_name?.trim();
      return `Setter (${name || "Unknown"})`;
    }
    if (senderType === "lead") return "Lead";
    // 'system' or any other value
    return "[System]";
  } else {
    // Normalized shape — no ghl_sender_user_id available
    const sender = msg.sender ?? "system";
    if (sender === "setter") return "Setter";
    if (sender === "lead") return "Lead";
    return "[System]";
  }
}

// ---------------------------------------------------------------------------
// Null text fallback
// ---------------------------------------------------------------------------

function getTextFallback(msg: SerializerMessage): string {
  const senderType = isRawMessage(msg)
    ? (msg.sender_type?.toLowerCase() ?? "")
    : (msg.sender ?? "");
  const hasMedia = msg.has_media ?? false;

  if (senderType === "lead") {
    return hasMedia ? "[Image sent]" : "[Empty message]";
  }
  if (senderType === "setter") {
    return hasMedia ? "[Image sent]" : "[ManyChat automation]";
  }
  // system or unknown
  return "[System message]";
}

// ---------------------------------------------------------------------------
// Filter: strip CVM system messages (Opportunity created/updated/deleted)
// Defensive — caller should pre-filter, but don't trust it.
// ---------------------------------------------------------------------------

const CVM_PATTERN = /^Opportunity (created|updated|deleted)/i;

function shouldExclude(msg: SerializerMessage): boolean {
  const senderType = isRawMessage(msg)
    ? (msg.sender_type?.toLowerCase() ?? "")
    : (msg.sender ?? "");
  const text = isRawMessage(msg) ? msg.message_text : msg.text;

  if (senderType === "system" && text && CVM_PATTERN.test(text.trim())) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Main serializer
// ---------------------------------------------------------------------------

export function serializeConversation(args: {
  messages: SerializerMessage[];
  contactName: string;
  leadInstagram?: string | null;
  timezone: string;
  orgName?: string | null;
}): string {
  const { contactName, leadInstagram, timezone, orgName } = args;

  // Sort messages by sent_at ASC (defensive — caller should already sort)
  const sorted = [...args.messages].sort(
    (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
  );

  // Filter out CVM system messages
  const messages = sorted.filter((msg) => !shouldExclude(msg));

  // Build header line 1: contact name + optional instagram handle
  const instagramSuffix = leadInstagram ? ` (@${leadInstagram})` : "";
  const headerLine1 = `Conversation with ${contactName}${instagramSuffix}`;

  // Header line 2: org/timezone context
  const headerLine2 = orgName ? `Org: ${orgName} · ${timezone}` : `Timezone: ${timezone}`;

  // Empty messages case
  if (messages.length === 0) {
    return `${headerLine1}\n${headerLine2}\n\nNo messages to copy.`;
  }

  // Date range for header line 3
  const firstDate = formatDateRange(messages[0].sent_at, timezone);
  const lastDate = formatDateRange(messages[messages.length - 1].sent_at, timezone);

  // Serialize each message
  const lines: string[] = [];
  for (const msg of messages) {
    const label = getSenderLabel(msg);
    const rawText = isRawMessage(msg) ? msg.message_text : msg.text;
    const text = rawText && rawText.trim() ? rawText : getTextFallback(msg);
    const ts = formatTimestamp(msg.sent_at, timezone);
    lines.push(`[${ts}] ${label}: ${text}`);
  }

  return `${headerLine1}\n${headerLine2}\nMessages: ${messages.length} · ${firstDate} – ${lastDate}\n\n${lines.join("\n")}`;
}
